// Simple authentication service for protecting movie database operations
export interface AuthUser {
  id: string;
  email: string;
  name: string;
}

class AuthService {
  private isAuthenticated = false;
  private user: AuthUser | null = null;

  // Check if user is authenticated
  isLoggedIn(): boolean {
    return this.isAuthenticated;
  }

  // Get current user
  getCurrentUser(): AuthUser | null {
    return this.user;
  }

  // Login with password
  async login(password: string): Promise<boolean> {
    try {
      // In production, this should be a secure API call
      // For now, we'll use a simple password check
      const correctPassword = import.meta.env.VITE_ADMIN_PASSWORD;
      
      if (password === correctPassword) {
        this.isAuthenticated = true;
        this.user = {
          id: 'admin',
          email: 'admin@joao-males.org',
          name: 'João Malés'
        };
        
        // Store authentication state in localStorage
        localStorage.setItem('movieDbAuth', JSON.stringify({
          isAuthenticated: true,
          user: this.user,
          timestamp: Date.now()
        }));
        
        return true;
      }
      return false;
    } catch (error) {
      console.error('Login error:', error);
      return false;
    }
  }

  // Logout
  logout(): void {
    this.isAuthenticated = false;
    this.user = null;
    localStorage.removeItem('movieDbAuth');
  }

  // Check if stored authentication is still valid
  checkStoredAuth(): boolean {
    try {
      const stored = localStorage.getItem('movieDbAuth');
      if (!stored) return false;

      const auth = JSON.parse(stored);
      const now = Date.now();
      const maxAge = 24 * 60 * 60 * 1000; // 24 hours

      // Check if authentication is still valid (not expired)
      if (auth.timestamp && (now - auth.timestamp) < maxAge) {
        this.isAuthenticated = auth.isAuthenticated;
        this.user = auth.user;
        return this.isAuthenticated;
      } else {
        // Authentication expired, clear it
        this.logout();
        return false;
      }
    } catch (error) {
      console.error('Error checking stored auth:', error);
      this.logout();
      return false;
    }
  }

  // Get auth headers for API calls
  getAuthHeaders(): Record<string, string> {
    if (!this.isAuthenticated) {
      return {};
    }
    
    return {
      'Authorization': `Bearer ${this.getAuthToken()}`,
      'Content-Type': 'application/json'
    };
  }

  // Get auth token (simple implementation)
  private getAuthToken(): string {
    return btoa(`admin:${Date.now()}`); // Simple token for demo
  }
}

// Export singleton instance
export const authService = new AuthService();

// Initialize auth service on app start
authService.checkStoredAuth();
